import { version as appVersion } from '../package.json'

const RELOAD_FLAG = 'sp_update_reload'
const baseUrl = process.env.BASE_URL || '/'
const VERSION_URL = `${baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`}version.json`

function alreadyReloaded() {
  try {
    return sessionStorage.getItem(RELOAD_FLAG) === '1'
  } catch (e) {
    return false
  }
}

function markReloaded() {
  try {
    sessionStorage.setItem(RELOAD_FLAG, '1')
  } catch (e) {
    // ignore
  }
}

function clearReloadFlag() {
  try {
    sessionStorage.removeItem(RELOAD_FLAG)
  } catch (e) {
    // ignore
  }
}

function hardReload() {
  if (alreadyReloaded()) return
  markReloaded()
  window.location.reload()
}

function isChunkLoadError(error) {
  if (!error) return false
  const name = (typeof error === 'object' && error.name) || ''
  const message = (typeof error === 'object' && error.message) || String(error)
  return (
    name === 'ChunkLoadError' ||
    /Loading chunk [\d]+ failed/i.test(message) ||
    /Loading CSS chunk .+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  )
}

/**
 * Firebase Hosting rewrites missing hashed assets to index.html (200 + HTML).
 * The browser then parses that HTML as JS/CSS and throws SyntaxError: Unexpected token '<'.
 * That path never becomes ChunkLoadError, so recover via hard reload here too.
 */
function isStaleAssetHtmlParseError(error, event) {
  if (!error && !event) return false
  const message =
    (typeof error === 'object' && error && error.message) ||
    (typeof error === 'string' ? error : '') ||
    (event && event.message) ||
    ''
  const filename = (event && event.filename) || ''
  const looksLikeBundledAsset =
    /\.(js|css)(\?|#|$)/i.test(filename) || /\/(js|css)\//i.test(filename)
  const unexpectedHtmlToken = /Unexpected token ['"]?</.test(message)
  return looksLikeBundledAsset && unexpectedHtmlToken
}

async function checkDeployedVersion() {
  try {
    const response = await fetch(VERSION_URL, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) return
    const data = await response.json()
    if (data && data.version && data.version !== appVersion) {
      hardReload()
    } else {
      clearReloadFlag()
    }
  } catch (e) {
    // Offline or transient network — skip
  }
}

/**
 * Recover from stale hashed chunks and force reload when a newer deploy is live.
 */
export function initUpdateGuard() {
  window.addEventListener('error', (event) => {
    if (
      isChunkLoadError(event.error) ||
      isChunkLoadError(event.message) ||
      isStaleAssetHtmlParseError(event.error, event)
    ) {
      hardReload()
    }
  })

  window.addEventListener('unhandledrejection', (event) => {
    if (
      isChunkLoadError(event.reason) ||
      isStaleAssetHtmlParseError(event.reason, null)
    ) {
      hardReload()
    }
  })

  checkDeployedVersion()

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      checkDeployedVersion()
    }
  })

  window.addEventListener('focus', () => {
    checkDeployedVersion()
  })
}
